import { useState, useCallback } from 'react';
import { Billing, getEntityType, MarketplaceOffer, MeteringAlgorithm, Plan } from '@the-real-insight/tri-model';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../../State';
import { userAPI } from '../../UserAPI';

const getPlanIdentity = (plan: any) => plan?._id ?? plan?.id ?? null;
const normalizeBillings = (plan: Plan) => {
    const existing = plan.billings?.[0];
    if (existing) {
        return [new Billing(existing.name || '', existing.fee || 0, existing.offset || 0, existing.algorithm || MeteringAlgorithm.perNamedUser)];
    }
    return [new Billing('', 0, 0, MeteringAlgorithm.perNamedUser)];
};

interface UsePlanDialogProps {
    type: any;
    marketplaceOffer: MarketplaceOffer;
    setMarketplaceOffer: (marketplaceOffer: MarketplaceOffer) => void;
}

interface UsePlanDialogReturn {
    planDialogOpen: boolean;
    selectedPlan: Plan | null;
    openPlanDialog: (plan?: Plan) => void;
    closePlanDialog: () => void;
    savePlan: () => Promise<void>;
    updatePlan: (updates: Partial<Plan>) => void;
    handleNameChange: (e: any, value?: string) => void;
    handleDescriptionChange: (e: any, value?: string) => void;
    handleBaseFeeChange: (e: any, value?: string) => void;
    handleIncludedRequestsChange: (e: any, value?: string) => void;
    handleFeePerAdditionalRequestChange: (e: any, value?: string) => void;
    handleIncludedUsersChange: (e: any, value?: string) => void;
    handleMonthlyFeeChange: (e: any, value?: string) => void;
    handleTermChange: (e: any, option?: any) => void;
    handleValidFromChange: (date: Date | null | undefined) => void;
    handleBillingModelChange: (e: any, option?: any) => void;
    customerCode: string;
    customerCodeError: string | null;
    customerDetails: { name: string; address: any } | null;
    handleCustomerCodeChange: (e: any, value?: string) => void;
    // Voucher dialog (shown after plan is saved)
    voucherDialogOpen: boolean;
    voucher: string | null;
    savedPlanForVoucher: Plan | null;
    closeVoucherDialog: () => void;
    handleCopyVoucher: () => void;
    handleCopyVoucherLink: () => void;
    handleEmailVoucher: () => Promise<void>;
    loadingVoucher: boolean;
}

export function usePlanDialog({ type, marketplaceOffer, setMarketplaceOffer }: UsePlanDialogProps): UsePlanDialogReturn {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [customerCode, setCustomerCode] = useState('');
    const [customerCodeError, setCustomerCodeError] = useState<string | null>(null);
    const [customerDetails, setCustomerDetails] = useState<{ name: string; address: any } | null>(null);
    const [_loadingCustomer, setLoadingCustomer] = useState(false);
    
    // Voucher dialog state (shown after plan is saved)
    const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
    const [voucher, setVoucher] = useState<string | null>(null);
    const [savedPlanForVoucher, setSavedPlanForVoucher] = useState<Plan | null>(null);
    const [loadingVoucher, setLoadingVoucher] = useState(false);

    // Validate customer code format: XXXX-XXXX-XXXX (alphanumeric, uppercase)
    const validateCustomerCodeFormat = (code: string): boolean => {
        const pattern = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
        return pattern.test(code.toUpperCase());
    };

    // Check if input is an email format
    const isEmailFormat = (input: string): boolean => {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(input);
    };

    // Only validate customer code - don't create voucher yet
    const handleCustomerCodeChange = useCallback(async (e: any, value?: string) => {
        const input = (value || '').trim();
        setCustomerCode(input);
        setCustomerCodeError(null);
        setCustomerDetails(null);

        if (!input) {
            return;
        }

        const isEmail = isEmailFormat(input);

        // If it's an email, just validate and show as customer detail
        if (isEmail) {
            setCustomerDetails({ name: input, address: null });
            return;
        }

        // Otherwise, treat as customer ID - validate format
        const code = input.toUpperCase();
        if (!validateCustomerCodeFormat(code)) {
            setCustomerCodeError(t('ui.neitherCustomerIdNorEmail', 'Is neither a correct customer ID (format XXXX-XXXX-XXXX) nor contact e-mail'));
            return;
        }

        // Fetch customer details (but don't create voucher yet - that happens after save)
        setLoadingCustomer(true);
        try {
            const details = await userAPI.getCustomerDetails(code);
            setCustomerDetails(details);
            setCustomerCodeError(null);
        } catch (error: any) {
            console.error('Failed to fetch customer details:', error);
            setCustomerCodeError(t('ui.neitherCustomerIdNorEmail', 'Is neither a correct customer ID (format XXXX-XXXX-XXXX) nor contact e-mail'));
            setCustomerDetails(null);
        } finally {
            setLoadingCustomer(false);
        }
    }, [t]);
    
    const closeVoucherDialog = useCallback(() => {
        setVoucherDialogOpen(false);
        setVoucher(null);
        setSavedPlanForVoucher(null);
    }, []);

    const openPlanDialog = (plan?: Plan) => {
        if (plan) {
            const normalizedPlan = {
                ...plan,
                billings: normalizeBillings(plan),
            };
            setSelectedPlan(normalizedPlan);
            // Restore customer code if plan has one
            if ((plan as any).customer) {
                setCustomerCode((plan as any).customer);
                // Fetch customer details if customer code exists
                handleCustomerCodeChange(null, (plan as any).customer);
            } else {
                setCustomerCode('');
                setCustomerDetails(null);
                setVoucher(null);
            }
        } else {
            const plan = new Plan({
                name: 'Basis 3',
                description: 'Monatliche Grundgebühr von 15€ für 3 Nutzer, abzuschließen für 1 Jahr und zahlbar bei Abschluss, 5€ für jeden registrierten Nutzenden ab dem 4. Nutzenden in monatlicher Abrechnung.',
                baseFee: 15,
                term: 12,
                billings: [new Billing('', 5, 3, MeteringAlgorithm.perNamedUser)],
                validFromDate: new Date(),
                validToDate: dayjs().add(1, 'year').toDate(),
            })
            setSelectedPlan(plan);
            setCustomerCode('');
            setCustomerDetails(null);
            setVoucher(null);
        }
        setPlanDialogOpen(true);
    };

    const closePlanDialog = useCallback(() => {
        setPlanDialogOpen(false);
        setSelectedPlan(null);
        setCustomerCode('');
        setCustomerCodeError(null);
        setCustomerDetails(null);
        setVoucher(null);
    }, []);

    const savePlan = useCallback(async () => {
        const currentPlan = selectedPlan;
        if (!currentPlan) return;

        // Always store customer field on the plan (even if empty)
        // Keep the billing model only in baseFee + billings[0]
        const normalizedBillings = normalizeBillings(currentPlan);
        const { includedRequests, feePerAdditionalRequest, ...rest } = currentPlan as any;
        const planWithCustomer = {
            ...rest,
            customer: customerCode || null,
            billings: normalizedBillings,
        } as any;

        const updatedPlans = [...marketplaceOffer.plans];
        const currentPlanId = getPlanIdentity(currentPlan);
        const planIndex = updatedPlans.findIndex((plan) => {
            const planId = getPlanIdentity(plan);
            if (currentPlanId && planId) {
                return planId.toString() === currentPlanId.toString();
            }
            return plan === currentPlan;
        });

        if (planIndex === -1) {
            updatedPlans.push(planWithCustomer);
        } else {
            updatedPlans[planIndex] = planWithCustomer;
        }

        // Get entity type name - prefer _type from marketplaceOffer, fallback to type prop
        const entityTypeName = marketplaceOffer._type || (typeof type === 'string' ? type : type?.name);
        if (!entityTypeName) {
            console.error('Cannot determine entity type for marketplace offer');
            throw new Error('Cannot determine entity type for marketplace offer');
        }

        const updatedMarketplaceOffer = new (getEntityType(entityTypeName).type.type)({
            ...marketplaceOffer,
            plans: updatedPlans
        });

        // Update local state first
        setMarketplaceOffer(updatedMarketplaceOffer);
        
        // Save to backend if marketplace offer has an ID and we have authentication
        if (updatedMarketplaceOffer._id && authentication?.customer) {
            try {
                await userAPI.updateMarketplaceOffer(
                    authentication.customer,
                    updatedMarketplaceOffer
                );
            } catch (error) {
                console.error('Failed to save marketplace offer:', error);
                throw error;
            }
        }
        
        // Close the plan dialog
        setPlanDialogOpen(false);
        setSelectedPlan(null);
        
        // If there's a customer code, create voucher and show voucher dialog
        if (customerCode && updatedMarketplaceOffer._id && authentication?.customer) {
            const isEmail = isEmailFormat(customerCode);
            setLoadingVoucher(true);
            setSavedPlanForVoucher(planWithCustomer);
            setVoucherDialogOpen(true);
            
            try {
                const voucherToken = await userAPI.createMarketplaceOfferVoucher(
                    authentication.customer,
                    updatedMarketplaceOffer._id,
                    customerCode,
                    isEmail,
                    planWithCustomer // Include the saved plan in the voucher
                );
                setVoucher(voucherToken);
            } catch (voucherError: any) {
                console.error('Failed to create voucher:', voucherError);
                // Still show the dialog but with error
                setVoucher(null);
            } finally {
                setLoadingVoucher(false);
            }
        }
    }, [selectedPlan, marketplaceOffer, type, setMarketplaceOffer, customerCode, authentication]);

    const updatePlan = useCallback((updates: Partial<Plan>) => {
        setSelectedPlan(prevPlan => {
            if (!prevPlan) return prevPlan;
            return { ...prevPlan, ...updates };
        });
    }, []);

    const handleNameChange = useCallback((e: any, value?: string) => {
        updatePlan({ name: value || '' });
    }, [updatePlan]);

    const handleDescriptionChange = useCallback((e: any, value?: string) => {
        updatePlan({ description: value || '' });
    }, [updatePlan]);

    const handleBaseFeeChange = useCallback((e: any, value?: string) => {
        updatePlan({ baseFee: value ? parseFloat(value) : 0 });
    }, [updatePlan]);

    const handleIncludedRequestsChange = useCallback((e: any, value?: string) => {
        const numValue = value ? parseInt(value) : 1;
        setSelectedPlan(prevPlan => {
            if (!prevPlan) return prevPlan;
            const currentAlgorithm = prevPlan.billings?.[0]?.algorithm || MeteringAlgorithm.perNamedUser;
            return {
                ...prevPlan,
                billings: [new Billing('', prevPlan.billings?.[0]?.fee || 0, numValue, currentAlgorithm)]
            };
        });
    }, []);

    const handleFeePerAdditionalRequestChange = useCallback((e: any, value?: string) => {
        const numValue = value ? parseFloat(value) : 0;
        setSelectedPlan(prevPlan => {
            if (!prevPlan) return prevPlan;
            const currentAlgorithm = prevPlan.billings?.[0]?.algorithm || MeteringAlgorithm.perNamedUser;
            return {
                ...prevPlan,
                billings: [new Billing('', numValue, prevPlan.billings?.[0]?.offset || 0, currentAlgorithm)]
            };
        });
    }, []);

    const handleIncludedUsersChange = useCallback((e: any, value?: string) => {
        const numValue = value ? parseInt(value) : 1;
        setSelectedPlan(prevPlan => {
            if (!prevPlan) return prevPlan;
            const currentAlgorithm = prevPlan.billings?.[0]?.algorithm || MeteringAlgorithm.perNamedUser;
            return {
                ...prevPlan,
                billings: [new Billing('', prevPlan.billings?.[0]?.fee || 0, numValue, currentAlgorithm)]
            };
        });
    }, []);

    const handleMonthlyFeeChange = useCallback((e: any, value?: string) => {
        const numValue = value ? parseFloat(value) : 0;
        setSelectedPlan(prevPlan => {
            if (!prevPlan) return prevPlan;
            const currentAlgorithm = prevPlan.billings?.[0]?.algorithm || MeteringAlgorithm.perNamedUser;
            return {
                ...prevPlan,
                billings: [new Billing('', numValue, prevPlan.billings?.[0]?.offset || 0, currentAlgorithm)]
            };
        });
    }, []);

    const handleTermChange = useCallback((e: any, option?: any) => {
        if (option?.key) {
            updatePlan({ term: parseInt(String(option.key)) });
        }
    }, [updatePlan]);

    const handleValidFromChange = useCallback((date: Date | null | undefined) => {
        if (date) {
            updatePlan({ validFromDate: date });
        }
    }, [updatePlan]);

    const handleBillingModelChange = useCallback((e: any, option?: any) => {
        if (option?.key) {
            const algorithm = option.key as MeteringAlgorithm;
            setSelectedPlan(prevPlan => {
                if (!prevPlan) return prevPlan;
                return {
                    ...prevPlan,
                    billings: [new Billing('', prevPlan.billings?.[0]?.fee || 0, prevPlan.billings?.[0]?.offset || 0, algorithm)]
                };
            });
        }
    }, []);

    const handleCopyVoucher = useCallback(async () => {
        if (voucher) {
            try {
                await navigator.clipboard.writeText(voucher);
            } catch (error) {
                console.error('Failed to copy voucher:', error);
            }
        }
    }, [voucher]);

    const handleCopyVoucherLink = useCallback(async () => {
        if (voucher && marketplaceOffer._id) {
            try {
                const link = `https://www.real-gain.org/marketplace/${marketplaceOffer._id}?voucher=${voucher}`;
                await navigator.clipboard.writeText(link);
            } catch (error) {
                console.error('Failed to copy voucher link:', error);
            }
        }
    }, [voucher, marketplaceOffer._id]);

    const handleEmailVoucher = useCallback(async () => {
        if (!customerCode || !marketplaceOffer._id || !authentication?.customer || !savedPlanForVoucher) {
            console.error('Missing required data for sending voucher email');
            return;
        }

        const isEmail = isEmailFormat(customerCode);

        try {
            await userAPI.sendMarketplaceOfferVoucherEmail(
                authentication.customer,
                marketplaceOffer._id,
                customerCode,
                isEmail,
                savedPlanForVoucher // Include the saved plan in the voucher
            );
        } catch (error: any) {
            console.error('Failed to send voucher email:', error);
            throw error;
        }
    }, [customerCode, marketplaceOffer._id, authentication, savedPlanForVoucher]);

    return {
        planDialogOpen,
        selectedPlan,
        openPlanDialog,
        closePlanDialog,
        savePlan,
        updatePlan,
        handleNameChange,
        handleDescriptionChange,
        handleBaseFeeChange,
        handleIncludedRequestsChange,
        handleFeePerAdditionalRequestChange,
        handleIncludedUsersChange,
        handleMonthlyFeeChange,
        handleTermChange,
        handleValidFromChange,
        handleBillingModelChange,
        customerCode,
        customerCodeError,
        customerDetails,
        handleCustomerCodeChange,
        // Voucher dialog
        voucherDialogOpen,
        voucher,
        savedPlanForVoucher,
        closeVoucherDialog,
        handleCopyVoucher,
        handleCopyVoucherLink,
        handleEmailVoucher,
        loadingVoucher,
    };
} 